'use strict';

import WebAPIUtils from '../WebAPIUtils';
import {
  DATA_INTEGRATION_CLAAS_CONNECT,
  DATA_INTEGRATION_EXATREK,
} from './DataIntegrationsReducer';

/* ============== //
 ||     TYPES     ||
 // ============== */

const GET_EXATREK_MAPS = 'fieldsense/YieldReducer/GET_EXATREK_MAPS';
const GET_ALL_CLAAS_YIELD_DATA = 'fieldsense/YieldReducer/GET_ALL_CLAAS_YIELD_DATA';
const GET_CLAAS_YIELD_VALUES = 'fieldsense/YieldReducer/GET_CLAAS_YIELD_VALUES';
const GET_EXATREK_YIELD_VALUES = 'fieldsense/YieldReducer/GET_EXATREK_YIELD_VALUES';
const CLEAR_FIELD_YIELD_DATA = 'fieldsense/YieldReducer/CLEAR_YIELD_DATA';

/* ============== //
||    HELPERS     ||
// ============== */

function mergeYieldData(existing, newData, source) {
  const result = { ...existing };

  Object.keys(newData).forEach((fieldId) => {
    const mappedAssets = newData[fieldId].map((asset) => {
      const normalized = {
        id: asset.id,
        date: asset.startDate || asset.date,
        url: asset.imageUrl || asset.url,
        colorIntervals: asset.colorIntervals,
        seasonId: asset.seasonId,
        source: source,
      };

      return normalized;
    });

    const sorted = mappedAssets.sort((a, b) => new Date(b.date) - new Date(a.date));

    if (result[fieldId]) {
      result[fieldId] = [...result[fieldId], ...sorted].sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );
    } else {
      result[fieldId] = sorted;
    }
  });

  return result;
}

function replaceExatrekYieldData(existing, exatrekMaps) {
  const exatrekYieldByField = {};

  exatrekMaps.forEach((exatrekMap) => {
    if (exatrekMap.mapType.toUpperCase() !== 'HARVEST') {
      return;
    }

    const yieldLayer = exatrekMap.layers.find(({ layer }) => layer.toUpperCase() === 'YIELD');
    if (!yieldLayer) {
      return;
    }

    const fieldAssets = exatrekYieldByField[exatrekMap.fieldId] || [];
    fieldAssets.push({
      id: exatrekMap.id,
      date: exatrekMap.startDate,
      url: yieldLayer.imageUrl,
      valuesUrl: yieldLayer.valuesUrl,
      colorIntervals: yieldLayer.colorIntervals,
      seasonId: exatrekMap.seasonId,
      source: DATA_INTEGRATION_EXATREK,
    });
    exatrekYieldByField[exatrekMap.fieldId] = fieldAssets;
  });

  const yieldDataByField = Object.fromEntries(
    Object.entries(existing)
      .map(([fieldId, fieldAssets]) => [
        fieldId,
        fieldAssets.filter((asset) => asset.source !== DATA_INTEGRATION_EXATREK),
      ])
      .filter(([, fieldAssets]) => fieldAssets.length > 0)
  );

  Object.entries(exatrekYieldByField).forEach(([fieldId, fieldAssets]) => {
    yieldDataByField[fieldId] = [...(yieldDataByField[fieldId] || []), ...fieldAssets].sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
  });

  return yieldDataByField;
}

/* ============== //
||    ACTIONS    ||
// ============== */

export function getExatrekMaps(farmId) {
  return {
    type: GET_EXATREK_MAPS,
    payload: WebAPIUtils.getExatrekMaps(farmId),
  };
}

export function getExatrekYieldValues(exatrekMapId) {
  return async (dispatch, getState) => {
    const { yieldData } = getState().yield;

    try {
      const yieldAsset = Object.values(yieldData).reduce(
        (matchedAsset, fieldAssets) =>
          matchedAsset ||
          fieldAssets.find(
            (asset) => asset.source === DATA_INTEGRATION_EXATREK && asset.id === exatrekMapId
          ),
        null
      );
      if (!yieldAsset) {
        throw new Error(`Exatrek map ${exatrekMapId} is not available`);
      }

      const values = yieldAsset.yieldValues
        ? { values: yieldAsset.yieldValues }
        : await WebAPIUtils.getExatrekYieldLayerValues(yieldAsset.valuesUrl);
      dispatch({
        type: GET_EXATREK_YIELD_VALUES + '_FULFILLED',
        meta: { exatrekMapId },
        payload: values,
      });
      return values;
    } catch (error) {
      dispatch({
        type: GET_EXATREK_YIELD_VALUES + '_REJECTED',
        meta: { exatrekMapId },
        error,
      });
      throw error;
    }
  };
}

export function getAllClaasYieldData(farmId, fields) {
  return {
    type: GET_ALL_CLAAS_YIELD_DATA,
    payload: WebAPIUtils.getAllClaasYieldData(farmId),
  };
}

export function getClaasYieldValues(farmId, claasMapId) {
  return async (dispatch, getState) => {
    const { yieldData } = getState().yield;
    let cachedValues = null;

    /* TODO: When claas color intervals are fetched from sign in, Replace cache logic with*/
    /*
    Object.values(yieldData).forEach((fieldAssets) => {
      fieldAssets.forEach((asset) => {
        if (asset.id === claasMapId && asset.yieldValues) {
          cachedValues = asset.yieldValues;
        }
      });
    });

    if (cachedValues) {
      dispatch({
        type: GET_CLAAS_YIELD_VALUES + '_FULFILLED',
        meta: { claasMapId },
        payload: { values: cachedValues },
      });
      return {values: cachedValues};
    }
     */

    Object.values(yieldData).forEach((fieldAssets) => {
      fieldAssets.forEach((asset) => {
        if (asset.id === claasMapId && asset.yieldValues && asset.colorIntervals) {
          cachedValues = asset;
        }
      });
    });

    if (cachedValues) {
      const cachedPayload = {
        values: cachedValues.yieldValues,
        colorIntervals: cachedValues.colorIntervals,
      };

      dispatch({
        type: GET_CLAAS_YIELD_VALUES + '_FULFILLED',
        meta: { claasMapId },
        payload: cachedPayload,
      });
      return cachedPayload;
    }

    try {
      const values = await WebAPIUtils.getClaasYieldValues(farmId, claasMapId);
      dispatch({
        type: GET_CLAAS_YIELD_VALUES + '_FULFILLED',
        meta: { claasMapId },
        payload: values,
      });
      return values;
    } catch (error) {
      dispatch({
        type: GET_CLAAS_YIELD_VALUES + '_REJECTED',
        meta: { claasMapId },
        error,
      });
      throw error;
    }
  };
}

export function clearFieldYieldData(fieldId) {
  return {
    type: CLEAR_FIELD_YIELD_DATA,
    payload: fieldId,
  };
}

/* ============== //
 ||    REDUCER    ||
 // ============== */

const initState = {
  yieldData: {},
};

export default function reducer(state = initState, action) {
  switch (action.type) {
    case GET_EXATREK_MAPS + '_FULFILLED': {
      state = {
        ...state,
        yieldData: replaceExatrekYieldData(state.yieldData, action.payload),
      };
      break;
    }

    case GET_EXATREK_YIELD_VALUES + '_FULFILLED': {
      const payload = action.payload;
      const exatrekMapId = action.meta?.exatrekMapId;

      if (payload && exatrekMapId) {
        const updatedYieldData = { ...state.yieldData };
        Object.keys(updatedYieldData).forEach((fieldId) => {
          updatedYieldData[fieldId] = updatedYieldData[fieldId].map((asset) => {
            if (asset.source === DATA_INTEGRATION_EXATREK && asset.id === exatrekMapId) {
              return { ...asset, yieldValues: payload.values };
            }
            return asset;
          });
        });

        state = {
          ...state,
          yieldData: updatedYieldData,
        };
      }
      break;
    }

    case GET_ALL_CLAAS_YIELD_DATA + '_REJECTED': {
      state = { ...state, yieldData: {} };
      break;
    }

    case GET_ALL_CLAAS_YIELD_DATA + '_FULFILLED': {
      const payload = action.payload;
      if (payload && payload.interpolationAssetsByField) {
        state = {
          ...state,
          yieldData: mergeYieldData(
            state.yieldData,
            payload.interpolationAssetsByField,
            DATA_INTEGRATION_CLAAS_CONNECT
          ),
        };
      }
      break;
    }

    case GET_CLAAS_YIELD_VALUES + '_FULFILLED': {
      const payload = action.payload;
      const claasMapId = action.meta?.claasMapId;

      if (payload && claasMapId) {
        const updatedYieldData = { ...state.yieldData };
        Object.keys(updatedYieldData).forEach((fieldId) => {
          updatedYieldData[fieldId] = updatedYieldData[fieldId].map((asset) => {
            if (asset.id === claasMapId) {
              return {
                ...asset,
                yieldValues: payload.values,
                colorIntervals: payload.colorIntervals,
              };
            }
            return asset;
          });
        });

        state = {
          ...state,
          yieldData: updatedYieldData,
        };
      }
      break;
    }

    case CLEAR_FIELD_YIELD_DATA: {
      const fieldId = action.payload;
      const updatedYieldData = { ...state.yieldData };
      delete updatedYieldData[fieldId];

      return {
        ...state,
        yieldData: updatedYieldData,
      };
    }

    default:
      break;
  }
  return state;
}
